import { Injectable } from '@angular/core';
import { OidcSecurityService, LoginResponse } from 'angular-auth-oidc-client';
import { Router, RouterStateSnapshot } from '@angular/router';
import { Observable } from 'rxjs';
import { distinctUntilChanged, map, take } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  // Long-lived auth-state stream for services that cache per-user data and need to
  // invalidate on same-tab logout/login (impersonation, account switch).
  readonly authState$: Observable<boolean>;

  constructor(
    private router: Router,
    private oidcSecurityService: OidcSecurityService
  ) {
    this.authState$ = this.oidcSecurityService.isAuthenticated$.pipe(
      map(({ isAuthenticated }) => isAuthenticated),
      distinctUntilChanged()
    );
  }

  checkAuth(): Observable<LoginResponse> {
    return this.oidcSecurityService.checkAuth();
  }

  isLoggedIn(): boolean {
    let authenticated = false;
    this.oidcSecurityService.isAuthenticated$.pipe(take(1)).subscribe(
      ({ isAuthenticated }) => authenticated = isAuthenticated
    );
    return authenticated;
  }

  isLoggedIn$(): Observable<boolean> {
    return this.oidcSecurityService.isAuthenticated$.pipe(
      take(1),
      map(({ isAuthenticated }) => isAuthenticated)
    );
  }

  getClaims(): any {
    let claims: any = {};
    this.oidcSecurityService.userData$.pipe(take(1)).subscribe(
      ({ userData }) => claims = userData ?? {}
    );
    return claims;
  }

  hasClaim(claim: string): boolean {
    const claims = this.getClaims();
    return claims !== null && claim in claims;
  }

  getAccessToken(): Observable<string> {
    return this.oidcSecurityService.getAccessToken();
  }

  isImpersonating(): boolean {
    const claims = this.getClaims();
    return !!claims?.impersonator_id;
  }

  getOriginalTenant(): string | null {
    const claims = this.getClaims();
    return claims?.original_tenant ?? null;
  }

  getCurrentTenant(): string | null {
    const claims = this.getClaims();
    return claims?.tenant ?? null;
  }

  /** Cold-start onboarding: returns the provisioning row id when a tenant DB hasn't been created yet. */
  getProvisioningId(): string | null {
    const claims = this.getClaims();
    return claims?.provisioning_id ?? null;
  }

  /** Pre-provisioning status reported in the JWT (Pending | UcpProvisioning | UcpReady | ChatSchemaReady | Failed). */
  getProvisioningStatus(): string | null {
    const claims = this.getClaims();
    return claims?.provisioning_status ?? null;
  }

  startAuthentication(route: RouterStateSnapshot) {
    this.oidcSecurityService.authorize();
  }

  logout() {
    this.oidcSecurityService.logoff().subscribe();
  }

  startLogOut() {
    this.oidcSecurityService.logoff().subscribe();
  }
}
